import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { Frame } from '@/types/location';
import { monthDayTime, prettyDate } from '@/utils/dates';
import CopyToClipboard from '@/components/ui/shared/CopyToClipboard';
import { useUser } from '@/hooks/useUser';
import { useScout } from '@/hooks/useScout';
import Quarantine from '@/components/ui/common/Quarantine';

interface Props {
  sortedSequences: Frame[][];
  activeSequence: Frame[];
  activeSequenceIndex: number;
  setActiveSequenceIndex: Dispatch<SetStateAction<number>>;
  activeFrameIndex: { value: number };
  setActiveFrameIndex: Dispatch<SetStateAction<{ value: number }>>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

const Frames: React.FC<Props> = ({
  sortedSequences,
  activeSequence,
  activeSequenceIndex,
  setActiveSequenceIndex,
  activeFrameIndex,
  setActiveFrameIndex,
  setShowModal,
}) => {
  const { user } = useUser();
  const { quarantined, setQuarantined } = useScout();

  const isLastFrame =
    activeFrameIndex.value === activeSequence.length - 1 &&
    activeSequenceIndex === sortedSequences.length - 1;

  const isFirstFrame =
    activeFrameIndex.value === 0 && activeSequenceIndex === 0;

  const handleNavigation = useCallback(
    (direction: 'left' | 'right') => {
      if (direction === 'right') {
        if (isLastFrame) return;

        if (activeFrameIndex.value === activeSequence.length - 1) {
          setActiveSequenceIndex(prevState => {
            return prevState === sortedSequences.length - 1
              ? prevState
              : prevState + 1;
          });
          setActiveFrameIndex({ value: 0 });
        } else {
          setActiveFrameIndex({ value: activeFrameIndex.value + 1 });
        }
      } else if (direction === 'left') {
        if (isFirstFrame) return;

        if (activeFrameIndex.value === 0) {
          setActiveSequenceIndex(prevState => {
            return prevState > 0 ? prevState - 1 : prevState;
          });

          setActiveFrameIndex({
            value: sortedSequences[activeSequenceIndex - 1]
              ? sortedSequences[activeSequenceIndex - 1].length - 1
              : 0,
          });
        } else {
          setActiveFrameIndex({ value: activeFrameIndex.value - 1 });
        }
      }
    },
    [
      activeFrameIndex.value,
      activeSequence.length,
      isFirstFrame,
      isLastFrame,
      setActiveFrameIndex,
      activeSequenceIndex,
      setActiveSequenceIndex,
      sortedSequences,
    ],
  );

  const eventKeyCallback = useCallback(
    (event: any) => {
      switch (event.key) {
        case 'Escape':
          setShowModal(false);
          break;
        case 'ArrowLeft':
          handleNavigation('left');
          break;
        case 'ArrowRight':
          handleNavigation('right');
          break;
        default:
          break;
      }
    },
    [handleNavigation, setShowModal],
  );

  useEffect(() => {
    document.addEventListener('keydown', eventKeyCallback, false);

    return () => {
      document.removeEventListener('keydown', eventKeyCallback, false);
    };
  }, [eventKeyCallback]);

  return (
    <div
      data-quarantined={quarantined.includes(
        activeSequence[activeFrameIndex.value]?.sequence,
      )}
      className="data-[quarantined=true]:opacity-50">
      {activeSequence[activeFrameIndex.value] && (
        <>
          <div className="absolute left-5 top-5 z-10 flex cursor-pointer items-center text-lg leading-none">
            <div className="mr-2">
              {activeSequence[activeFrameIndex.value]?.position.lat.toFixed(5)},{' '}
              {activeSequence[activeFrameIndex.value]?.position.lon.toFixed(5)}
            </div>
            <CopyToClipboard
              content={`${activeSequence[activeFrameIndex.value]?.position.lat}, ${activeSequence[activeFrameIndex.value]?.position.lon}`}
            />
          </div>
          <div className="absolute bottom-5 right-5 z-10 text-lg">
            {activeFrameIndex.value + 1} / {activeSequence.length}
          </div>
          <div className="absolute bottom-5 left-5 z-10 flex flex-col gap-2 text-lg">
            {user?.admin && (
              <Quarantine
                quarantined={quarantined}
                setQuarantined={setQuarantined}
                activeSequence={activeSequence}
                activeFrameIndex={activeFrameIndex.value}
              />
            )}
            {prettyDate(activeSequence[activeFrameIndex.value].timestamp, true)}
            {', '}
            {monthDayTime(
              new Date(activeSequence[activeFrameIndex.value].timestamp),
            )}
          </div>
        </>
      )}
      <div className="flex h-dvh items-center justify-center">
        <TransformWrapper>
          <TransformComponent>
            <img
              className="max-h-dvh max-w-full"
              src={activeSequence[activeFrameIndex.value].url}
              alt="Active frame"
            />
          </TransformComponent>
        </TransformWrapper>
      </div>
    </div>
  );
};

export default Frames;
